import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    OnInit,
    signal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { unique as radashUnique } from 'radash';
import { firstValueFrom } from 'rxjs';
import { v4 } from 'uuid';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { AddLayerListInfoComponent } from '@connect-services/client/components/add-layer-list-info/add-layer-list-info.component';
import {
    ComplexModalTemplate,
    FIRST_COMPLEX_MODAL_STEP,
} from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { MapBaseSource, MapSourceTypeEnum } from '@connect-services/client/components/map/map.component';
import { SelectDepositStepComponent } from '@connect-services/client/components/modals/add-layer-modal/select-deposit-step/select-deposit-step.component';
import { SelectFluxStepComponent } from '@connect-services/client/components/modals/add-layer-modal/select-flux-step/select-flux-step.component';
import { SelectInterstandardStepComponent } from '@connect-services/client/components/modals/add-layer-modal/select-interstandard-step/select-interstandard-step.component';
import { SelectLayerStepComponent } from '@connect-services/client/components/modals/add-layer-modal/select-layer-step/select-layer-step.component';
import { SelectReferenceDataStepComponent } from '@connect-services/client/components/modals/add-layer-modal/select-reference-data-step/select-reference-data-step.component';
import { SelectReferenceDepositStepComponent } from '@connect-services/client/components/modals/add-layer-modal/select-reference-deposit-step/select-reference-deposit-step.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import {
    AddLayerSelectionType,
    AddLayerService,
    LayersPresetsExtended,
    TableInfo,
} from '@connect-services/client/services/add-layer.service';
import { DepositFolderService } from '@connect-services/client/services/deposit-folder.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import {
    DepositFolder,
    DepositFolderControllerGet200Response,
    DepositFoldersService,
    DepositsService,
    DepositTable,
    InterstandardsService,
    InterstandardStandardsStandard,
    InterstandardStandardsStandardService,
    Layer,
    Standard,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { ProgressBarComponent } from '@shared/client/components/progress-bar/progress-bar.component';
import {
    defaultPaginationParameters,
    OrderEnum,
    paginationMaxTake,
    PaginationParameters,
} from '@shared/client/models/pagination.model';

enum AddLayerStepEnum {
    SELECT_DATA_STEP = 'selectDataStep',
    SELECT_DEPOSIT_STEP = 'selectDepositStep',
    SELECT_LAYER_INFO_STEP = 'selectLayerInfoStep',
    SELECT_LAYER_NAME_STEP = 'selectLayerNameStep',
    SELECT_LAYER_TYPE_STEP = 'selectLayerTypeStep',
}

const defaultSteps: AddLayerStepEnum[] = [
    AddLayerStepEnum.SELECT_LAYER_TYPE_STEP,
    AddLayerStepEnum.SELECT_DATA_STEP,
    AddLayerStepEnum.SELECT_LAYER_NAME_STEP,
    AddLayerStepEnum.SELECT_LAYER_INFO_STEP,
];

const fluxPresetSteps: AddLayerStepEnum[] = [
    AddLayerStepEnum.SELECT_LAYER_TYPE_STEP,
    AddLayerStepEnum.SELECT_DATA_STEP,
    AddLayerStepEnum.SELECT_LAYER_INFO_STEP,
];

const referenceDataSteps: AddLayerStepEnum[] = [
    AddLayerStepEnum.SELECT_LAYER_TYPE_STEP,
    AddLayerStepEnum.SELECT_DATA_STEP,
    AddLayerStepEnum.SELECT_DEPOSIT_STEP,
    AddLayerStepEnum.SELECT_LAYER_NAME_STEP,
    AddLayerStepEnum.SELECT_LAYER_INFO_STEP,
];

export type AddLayerResult = {
    layers: Layer[];
    modalResult: ModalResultEnum;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AddLayerListInfoComponent,
        ComplexModalTemplate,
        ProgressBarComponent,
        RessourcesModule,
        SelectComponent,
        SelectDepositStepComponent,
        SelectFluxStepComponent,
        SelectInterstandardStepComponent,
        SelectLayerStepComponent,
        SelectReferenceDataStepComponent,
        SelectReferenceDepositStepComponent,
    ],
    selector: 'app-add-layer-modal',
    styleUrl: './add-layer-modal.component.scss',
    templateUrl: './add-layer-modal.component.html',
})
export class AddLayerModalComponent implements OnInit {
    protected readonly AddLayerSelectionType = AddLayerSelectionType;
    protected readonly addLayerService = inject(AddLayerService);
    protected readonly AddLayerStepEnum = AddLayerStepEnum;
    protected readonly allTablesByDeposit = signal<Record<string, TableInfo[]> | undefined>(undefined);
    protected readonly CheckboxState = CheckboxState;
    protected readonly currentStep = signal(FIRST_COMPLEX_MODAL_STEP);
    protected readonly currentStepEnum = computed<AddLayerStepEnum>(() => {
        const steps = this.currentSteps();

        return steps[this.currentStep() - 1];
    });
    protected readonly currentSteps = computed<AddLayerStepEnum[]>(() => {
        if (this.selectedLayerType() === AddLayerSelectionType.REFERENCE_DATA) {
            return referenceDataSteps;
        }

        if (
            this.selectedLayerType() === AddLayerSelectionType.FLUX &&
            this.addLayerService.selectedServiceCatalogs().length
        ) {
            return fluxPresetSteps;
        }

        return defaultSteps;
    });
    protected readonly currentTitle = computed<string>(() => {
        if (this.currentStep() === 1) {
            return this.translocoService.translate('addLayer');
        }

        switch (this.selectedLayerType()) {
            case AddLayerSelectionType.INTERSTANDARD:
                return this.translocoService.translate('addInterstandardLayer');
            case AddLayerSelectionType.TABLE:
                return this.translocoService.translate('addTableLayer');
            case AddLayerSelectionType.FLUX:
                return this.translocoService.translate('addFluxLayer');
            case AddLayerSelectionType.REFERENCE_DATA:
                return this.translocoService.translate('addReferenceDataLayer');
            default:
                return this.translocoService.translate('addLayer');
        }
    });
    protected readonly defaultLayers = computed(() =>
        this.addLayerService.getDefaultListValue(
            this.addLayerService.tableOptions(),
            this.addLayerService.selectedTablesInfo(),
        ),
    );
    protected dialogRef = inject(MatDialogRef<AddLayerModalComponent>);
    protected readonly displayErrorCreation = signal<boolean>(false);
    protected readonly existingProjectLayerNames = computed<string[]>(() =>
        this.sourcesService
            .sources()
            .filter((source: MapBaseSource<unknown>) => source.type === MapSourceTypeEnum.LAYER)
            .map((source: MapBaseSource<unknown>) => source.name),
    );
    protected readonly hasFieldInterventions = computed<boolean>(() => {
        return this.sourcesService.sources().some((source: MapBaseSource<unknown>) => {
            return source.type === MapSourceTypeEnum.FIELD_INTERVENTION;
        });
    });
    protected readonly isAddLayerLoading = signal<boolean>(false);
    protected readonly isLayerPreset = computed(
        () =>
            !this.currentLayerPresetTablesInfo().some(
                (layerPresetTableInfo) =>
                    !this.addLayerService
                        .selectedTablesInfo()
                        .map((tableInfo: TableInfo) => tableInfo.id)
                        .includes(layerPresetTableInfo.id),
            ),
    );
    protected readonly isSearchbarActive = computed<boolean>(() => {
        const selectedLayerType = this.selectedLayerType();

        return (
            this.currentStepEnum() === AddLayerStepEnum.SELECT_DATA_STEP &&
            !!selectedLayerType &&
            (selectedLayerType === AddLayerSelectionType.REFERENCE_DATA ||
                selectedLayerType === AddLayerSelectionType.FLUX)
        );
    });
    protected readonly isTableLoading = signal<boolean>(false);
    protected readonly Layer = Layer;
    protected readonly layerTypeDefaultValue = computed(() =>
        this.addLayerService.getDefaultValue(this.typeOption, this.selectedLayerType()),
    );
    protected readonly messageInformation = computed<string>(() => {
        if (this.isBboxMissingForReferenceDataCreation()) {
            return 'configureProjectBboxBeforeReferenceDataCreation';
        }
        const isReferenceDataFinalized =
            !!this.addLayerService.selectedDepositFolder() && !!this.addLayerService.selectedDeposit();
        const isSelectionDepositStep = this.currentStepEnum() === AddLayerStepEnum.SELECT_DEPOSIT_STEP;
        const isReferenceDataSelected = this.selectedLayerType() === AddLayerSelectionType.REFERENCE_DATA;

        return isSelectionDepositStep && isReferenceDataFinalized && isReferenceDataSelected
            ? 'selectedStandardsWillOnlyAppearInYourProjectArea'
            : '';
    });
    protected readonly Object = Object;
    protected readonly searchInput = signal<string>('');
    protected readonly searchLabelKey = computed<string>(() =>
        this.selectedLayerType() === AddLayerSelectionType.FLUX ? 'searchFlux' : 'searchData',
    );
    protected readonly selectedLayerType = signal<AddLayerSelectionType | undefined>(undefined);
    protected typeOption!: SelectOption<AddLayerSelectionType>[];
    private readonly canSelectDataStep = computed(() => {
        return !!this.selectedLayerType() && !this.isBboxMissingForReferenceDataCreation();
    });
    private readonly currentLayerPresetTablesInfo = signal<TableInfo[]>([]);
    private readonly depositFolderService = inject(DepositFolderService);
    private readonly depositServiceSdk = inject(DepositsService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly interstandardsService = inject(InterstandardsService);
    private readonly interstandardStandardsStandardService = inject(InterstandardStandardsStandardService);
    private readonly isBboxMissingForReferenceDataCreation = computed(() => {
        const bbox = this.projectBloc.value()?.bbox;

        return this.selectedLayerType() === AddLayerSelectionType.REFERENCE_DATA && !bbox;
    });
    private readonly projectBloc = inject(ProjectBloc);
    private readonly sdkDepositFoldersService = inject(DepositFoldersService);
    private readonly sourcesService = inject(SourcesService);
    private readonly standards = signal<Standard[]>([]);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        this.destroyRef.onDestroy(() => {
            this.addLayerService.resetSelectedData();

            this.selectedLayerType.set(undefined);
        });

        effect(() => {
            this.addLayerService.setExistingProjectLayerNames(this.existingProjectLayerNames());
        });
    }

    public canConfirm(): boolean {
        if (this.isAddLayerLoading()) {
            return false;
        }

        switch (this.currentStepEnum()) {
            case AddLayerStepEnum.SELECT_LAYER_TYPE_STEP:
                return this.canSelectDataStep();
            case AddLayerStepEnum.SELECT_DATA_STEP:
                switch (this.selectedLayerType()) {
                    case AddLayerSelectionType.INTERSTANDARD:
                        return !!this.addLayerService.selectedSecondInterstandardDepositFolder();
                    case AddLayerSelectionType.TABLE:
                        return !!this.addLayerService.selectedDeposit();
                    case AddLayerSelectionType.REFERENCE_DATA:
                        return !!this.addLayerService.selectedDepositFolder();
                    case AddLayerSelectionType.FLUX:
                        if (this.addLayerService.selectedServiceCatalogs().length) {
                            return true;
                        }

                        return (
                            !this.addLayerService.sourceUrlControl?.invalid &&
                            !!this.addLayerService.sourceUrlControl?.value &&
                            !!this.addLayerService.availableSourceLayers().length
                        );
                    default:
                        return false;
                }
            case AddLayerStepEnum.SELECT_DEPOSIT_STEP:
                return !!this.addLayerService.selectedDeposit();
            case AddLayerStepEnum.SELECT_LAYER_NAME_STEP:
                return !!this.addLayerService.selectedTablesInfo().length;
            case AddLayerStepEnum.SELECT_LAYER_INFO_STEP:
                return !this.addLayerService.layerFormRecord?.invalid && !!this.addLayerService.layersInfo();
            default:
                return false;
        }
    }

    public async changeCurrentStep(): Promise<void> {
        this.searchInput.set('');

        switch (this.currentStepEnum()) {
            case AddLayerStepEnum.SELECT_DATA_STEP:
                await this.initializeSecondStepDataByLayerType();
                break;
            case AddLayerStepEnum.SELECT_LAYER_NAME_STEP:
                await this.initializeTableNameByLayerType();
                break;
            case AddLayerStepEnum.SELECT_LAYER_INFO_STEP:
                if (
                    this.selectedLayerType() === AddLayerSelectionType.FLUX &&
                    this.addLayerService.selectedServiceCatalogs().length
                ) {
                    const hasResolvedAllSelectedServiceCatalogs =
                        await this.addLayerService.resolveSelectedServiceCatalogs();

                    if (!hasResolvedAllSelectedServiceCatalogs) {
                        this.currentStep.set(this.currentStep() - 1);

                        return;
                    }
                }
                this.addLayerService.initializeLayerInfo(this.selectedLayerType()!, this.hasFieldInterventions());
                break;
        }
    }

    public async closeModal(event: ModalResultEnum): Promise<void> {
        let layers: Layer[] = [];

        if (event === ModalResultEnum.CONFIRM) {
            this.displayErrorCreation.set(false);
            this.isAddLayerLoading.set(true);
            try {
                layers = await this.addLayerService.createLayers();
            } catch (error) {
                this.displayErrorCreation.set(true);
                throw error;
            }

            this.isAddLayerLoading.set(false);
        }

        this.dialogRef.close({ layers: layers, modalResult: event });
    }

    public handlePreselection(): void {
        const layerPresetTableNames = this.addLayerService
            .layersPresetsExtended()
            .map((layerPreset: LayersPresetsExtended) => layerPreset.tableName);

        this.currentLayerPresetTablesInfo.set(
            this.allTablesByDeposit()![this.addLayerService.selectedDeposit()!].reduce(
                (acc: TableInfo[], tableInfo) => {
                    if (layerPresetTableNames.includes(tableInfo.tableName)) {
                        acc.push(tableInfo);
                    }

                    return acc;
                },
                [],
            ),
        );

        if (this.isLayerPreset()) {
            this.addLayerService.selectedTablesInfo.set(
                this.addLayerService.selectedTablesInfo().filter(
                    (tableInfo: TableInfo) =>
                        !this.currentLayerPresetTablesInfo()
                            .map((tableInfo: TableInfo) => tableInfo.id)
                            .includes(tableInfo.id),
                ),
            );

            return;
        }

        const mergedMap = new Map<string, TableInfo>();

        [...this.currentLayerPresetTablesInfo(), ...this.addLayerService.selectedTablesInfo()].forEach(
            (tableInfo: TableInfo) => {
                mergedMap.set(tableInfo.id, tableInfo);
            },
        );

        this.addLayerService.selectedTablesInfo.set(Array.from(mergedMap.values()));
    }

    public ngOnInit(): void {
        const selectableTypes: AddLayerSelectionType[] = [
            AddLayerSelectionType.TABLE,
            AddLayerSelectionType.REFERENCE_DATA,
            AddLayerSelectionType.INTERSTANDARD,
            AddLayerSelectionType.FLUX,
        ];

        this.typeOption = selectableTypes.map((type: AddLayerSelectionType): SelectOption<AddLayerSelectionType> => ({
            label: this.translocoService.translate(type),
            value: type,
        }));
    }

    public selectType(layerTypeEnum: AddLayerSelectionType[]): void {
        this.selectedLayerType.set(layerTypeEnum[0]);

        this.addLayerService.sourceUrlControl = undefined;

        this.addLayerService.resetSelectedData();
    }

    private async fetchAllDepositFolder(): Promise<void> {
        this.addLayerService.isDepositFolderLoading.set(true);
        this.addLayerService.depositFolders.set(await this.depositFolderService.getAllDepositFolders());
        this.addLayerService.isDepositFolderLoading.set(false);
    }

    private async fetchAllDepositFolders(standard: Standard): Promise<DepositFolder[]> {
        let totalItems = 0;
        let hasNextPage = false;
        const paginationParameters: PaginationParameters = { ...defaultPaginationParameters };

        const depositFolders: DepositFolder[] = [];

        do {
            const paginatedDepositFolders: DepositFolderControllerGet200Response =
                await this.getPaginatedDepositFolders(standard, {
                    ...paginationParameters,
                    take: paginationMaxTake,
                });

            if (!totalItems) {
                totalItems = paginatedDepositFolders.totalItems;
            }
            hasNextPage = paginatedDepositFolders.hasNextPage;
            depositFolders.push(...paginatedDepositFolders.items);

            paginationParameters.page += 1;
        } while (hasNextPage);

        return depositFolders;
    }

    private async fetchDepositFolderByStandards(): Promise<void> {
        for (const standard of this.standards()) {
            const depositFolders: DepositFolder[] = await this.fetchAllDepositFolders(standard);

            this.addLayerService.depositFolderByStandard.set({
                [standard.id]: depositFolders,
                ...this.addLayerService.depositFolderByStandard(),
            });
        }
    }

    private async fetchInterstandardStandards(): Promise<void> {
        const interstandardStandardsStandards: InterstandardStandardsStandard[] = await firstValueFrom(
            this.interstandardStandardsStandardService.interstandardStandardsStandardControllerGetWithStandard(),
        );

        this.standards.set(
            radashUnique(
                interstandardStandardsStandards.map(
                    (interstandardStandardsStandard: InterstandardStandardsStandard) =>
                        interstandardStandardsStandard.standard,
                ),
                (standard) => standard.id,
            ),
        );

        await this.fetchDepositFolderByStandards();

        this.addLayerService.initSelectFirstInterstandardDepositFolder();
    }

    private async getAllLayerPresets(): Promise<void> {
        await this.addLayerService.setAllLayersPresets();

        if (this.addLayerService.selectedTablesInfo().length) {
            return;
        }

        this.handlePreselection();
    }

    private getDepositTables(): Promise<DepositTable[]> {
        return firstValueFrom(
            this.depositServiceSdk.depositControllerGetTables({
                id: this.addLayerService.selectedDeposit()!,
            }),
        );
    }

    private async getInterstandards(): Promise<void> {
        const standardId = this.addLayerService.selectedFirstInterstandardDepositFolder()!.standard!.id;
        const linkedStandardId = this.addLayerService.selectedSecondInterstandardDepositFolder()!.standard!.id;

        if (!standardId || !linkedStandardId) {
            return;
        }

        this.addLayerService.interstandards.set(
            await firstValueFrom(
                this.interstandardsService.interstandardControllerGetByStandards({
                    linkedStandardId: linkedStandardId,
                    standardId: standardId,
                }),
            ),
        );
    }

    private getPaginatedDepositFolders(
        standard: Standard,
        paginationParameters: Readonly<PaginationParameters>,
    ): Promise<DepositFolderControllerGet200Response> {
        return firstValueFrom(
            this.sdkDepositFoldersService.depositFolderControllerGet({
                ...paginationParameters,
                hasValidDeposit: true,
                isCaseSensitive: true,
                order: OrderEnum.ASC,
                orderBy: 'name',
                standard: standard.id,
            }),
        );
    }

    private async initializeSecondStepDataByLayerType(): Promise<void> {
        switch (this.selectedLayerType()) {
            case AddLayerSelectionType.TABLE:
                if (!this.addLayerService.depositFolders()?.length) {
                    await this.fetchAllDepositFolder();
                }
                break;
            case AddLayerSelectionType.INTERSTANDARD:
                if (!this.addLayerService.depositFolderByStandard()) {
                    await this.fetchInterstandardStandards();
                }
                break;
            case AddLayerSelectionType.FLUX:
                if (!this.addLayerService.sourceUrlControl) {
                    this.addLayerService.sourceUrlControl = new FormControl('', { nonNullable: true });
                }
        }
    }

    private async initializeTableNameByLayerType(): Promise<void> {
        this.isTableLoading.set(true);
        switch (this.selectedLayerType()) {
            case AddLayerSelectionType.TABLE:
            case AddLayerSelectionType.REFERENCE_DATA:
                if (!this.allTablesByDeposit()?.[this.addLayerService.selectedDeposit()!]?.length) {
                    const depositTables = await this.getDepositTables();

                    this.allTablesByDeposit.set({
                        ...this.allTablesByDeposit(),
                        [this.addLayerService.selectedDeposit()!]: depositTables.map((depositTable) => {
                            return {
                                hasGeom: depositTable.hasGeom,
                                id: v4(),
                                tableName: depositTable.name,
                            };
                        }),
                    });
                }

                this.initSelectTableOptionsForTableLayer();

                if (
                    this.addLayerService.depositStandard() ||
                    this.selectedLayerType() === AddLayerSelectionType.REFERENCE_DATA
                ) {
                    await this.getAllLayerPresets();
                }
                break;
            case AddLayerSelectionType.INTERSTANDARD:
                await this.getInterstandards();

                this.initSelectTableOptionsForInterstandardLayer();
                break;
            case AddLayerSelectionType.FLUX:
                if (this.addLayerService.tableOptions().length) {
                    return;
                }
                this.initSelectTableOptionsForFluxLayer();
                break;
        }
        this.isTableLoading.set(false);
    }

    private initSelectTableOptionsForFluxLayer(): void {
        this.addLayerService.tableOptions.set(
            this.addLayerService.availableSourceLayers().reduce((acc: SelectOption<TableInfo>[], urlSourceLayer) => {
                return [
                    ...acc,
                    {
                        label: urlSourceLayer.name,
                        value: { hasGeom: undefined, id: v4(), tableName: urlSourceLayer.name },
                    },
                ];
            }, []),
        );
    }

    private initSelectTableOptionsForInterstandardLayer(): void {
        this.addLayerService.tableOptions.set(
            this.addLayerService.interstandards().reduce((acc: SelectOption<TableInfo>[], interstandard) => {
                return [
                    ...acc,
                    {
                        hasGeom: interstandard.hasGeom,
                        label: interstandard.name,
                        trailingIcon: !interstandard.hasGeom ? ['far', 'table-cells'] : undefined,
                        value: {
                            hasGeom: interstandard.hasGeom,
                            id: interstandard.id,
                            tableName: interstandard.name,
                        },
                    },
                ];
            }, []),
        );
    }

    private initSelectTableOptionsForTableLayer(): void {
        const tableOptions: SelectOption<TableInfo>[] = this.allTablesByDeposit()![
            this.addLayerService.selectedDeposit()!
        ].reduce((acc: SelectOption<TableInfo>[], tableInfo) => {
            return [
                ...acc,
                {
                    hasGeom: tableInfo.hasGeom,
                    label: tableInfo.tableName,
                    trailingIcon: !tableInfo.hasGeom ? ['far', 'table-cells'] : undefined,
                    value: tableInfo,
                },
            ];
        }, []);

        this.addLayerService.tableOptions.set(
            tableOptions.sort((optionA: SelectOption<TableInfo>, optionB: SelectOption<TableInfo>) =>
                optionA.label.localeCompare(optionB.label),
            ),
        );
    }
}
